import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, MessageCircle, Phone, Wind, Shield, Music, UtensilsCrossed, Palette, BookOpen, Leaf, Mic, Globe, Users } from 'lucide-react';

const WA_URL = 'https://wa.me/919246887272?text=Hi%2C%20I%20am%20interested%20in%20booking%20a%20school%20tour%20at%20Sri%20Aurobindo%20Montessori%20School';

// ── Reusing images from the old ExtraCurricular page ──
const IMG_KARATE_1 = 'https://images.unsplash.com/photo-1555597673-b21d5c935865?w=700&q=85';
const IMG_KARATE_2 = 'https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?w=700&q=85';
const IMG_YOGA_1   = 'https://images.unsplash.com/photo-1599901860904-17e6ed7083a0?w=700&q=85';
const IMG_YOGA_2   = 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=700&q=85';
const IMG_ARTS_1   = 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=700&q=85';
const IMG_ARTS_2   = 'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?w=700&q=85';

const activities = [
  {
    icon: <Wind size={22} className="text-[#1E3A8A]" />,
    title: 'Yoga',
    age: 'From age 5',
    desc: 'Calm, balance and body-awareness — a natural complement to the Montessori work cycle.',
  },
  {
    icon: <Shield size={22} className="text-[#1E3A8A]" />,
    title: 'Karate',
    age: 'From age 5',
    desc: 'Discipline, focus and physical confidence. Respect, patience and self-control, practised every session.',
  },
  {
    icon: <Music size={22} className="text-[#1E3A8A]" />,
    title: 'Classical Vocal Music',
    age: 'From age 5',
    desc: 'Training the ear, the breath and the voice — and quietly building the foundation for all language learning.',
  },
  {
    icon: <UtensilsCrossed size={22} className="text-[#1E3A8A]" />,
    title: 'Cookery',
    age: 'All ages',
    desc: 'Real recipes, real tools, real pride. "Hands feed the mind" — and a child who cooks learns independence in the most direct way.',
  },
  {
    icon: <Palette size={22} className="text-[#1E3A8A]" />,
    title: 'Craft',
    age: 'All ages',
    desc: 'Fine motor skill, patience and creative expression — making things with care, from start to finish.',
  },
  {
    icon: <BookOpen size={22} className="text-[#1E3A8A]" />,
    title: 'Shloka Recitation & Bhagavad Gita',
    age: 'From age 5',
    desc: 'Clarity of speech (Sanskrit pronunciation makes every other language easier!), memory, and timeless values.',
  },
  {
    icon: <Leaf size={22} className="text-[#1E3A8A]" />,
    title: 'Care of the Environment',
    age: 'All ages',
    desc: 'Gardening, weeding, sweeping, washing up. The dignity of work, practised daily — not as a chore, but as a privilege.',
  },
];

const widerWorld = [
  {
    icon: <Globe size={20} className="text-[#1E3A8A]" />,
    title: 'Field trips, 3–4 times a year',
    desc: 'Learning traced to its roots, out in the real world. Parents who\'d like to join hands-on are warmly welcome.',
  },
  {
    icon: <Users size={20} className="text-[#1E3A8A]" />,
    title: 'Monthly workshops for Elementary children',
    desc: 'Hands-on, theme-based workshops that take learning beyond the classroom materials.',
  },
  {
    icon: <Mic size={20} className="text-[#1E3A8A]" />,
    title: 'Parent expert talks',
    desc: 'We invite parents who are accomplished in their fields to share their journeys with our older children. Real role models, at arm\'s length.',
  },
];

export default function ExtraCurricular() {
  return (
    <div className="pt-16">
      <Helmet>
        <title>Activities: Yoga, Karate, Music, Cookery &amp; More | Sri Aurobindo Montessori, Hyderabad</title>
        <meta name="description" content="Beyond the classroom at our Hyderabad Montessori school — yoga, karate, classical vocal music, public speaking from 2.5, cookery, craft, field trips, festivals of every faith, and an Annual Day run by children." />
      </Helmet>

      {/* ── BANNER ── */}
      <section className="bg-[#1E3A8A] py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300 mb-4">Activities</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-5" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Education is for life —<br className="hidden sm:block" /> so life is part of<br className="hidden sm:block" /> the education.
          </h1>
          <p className="text-blue-200 text-base max-w-2xl mx-auto">
            "Extracurricular" is a slightly wrong word — none of this is extra. Every activity below serves the same purpose as the classroom materials: a confident body, a clear voice, a thinking mind, a loving heart.
          </p>
        </div>
      </section>

      {/* ── WOVEN INTO EVERY WEEK ── */}
      <section className="py-20 bg-white" data-testid="activities-grid-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">Every Week</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1E3A8A]" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Woven into every week
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {activities.map((act, i) => (
              <div
                key={i}
                data-testid={`activity-card-${i}`}
                className="bg-[#F4F7FB] rounded-3xl p-7 hover:bg-[#EFF6FF] hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-11 h-11 bg-white rounded-2xl flex items-center justify-center mb-5 shadow-sm">
                  {act.icon}
                </div>
                <span className="text-xs font-bold text-[#60A5FA] uppercase tracking-wide">{act.age}</span>
                <h3 className="font-extrabold text-[#1E3A8A] text-base mt-1 mb-3 leading-snug" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  {act.title}
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed">{act.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── VOICE & CONFIDENCE ── */}
      <section className="py-20 bg-[#F4F7FB]" data-testid="voice-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">From Age 2.5</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1E3A8A] mb-6 leading-tight" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Voice and confidence,<br className="hidden sm:block" /> from age 2.5
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Show & Tell begins at two and a half. Long before a child could ever learn the words "stage fear", they are already standing before friends, holding something they love, and talking about it.
                </p>
                <p>
                  By Elementary, this grows into structured public speaking.
                </p>
              </div>
              <div className="mt-8 bg-white rounded-3xl px-7 py-6 border-l-4 border-[#1E3A8A] shadow-sm">
                <p className="text-[#1E3A8A] font-bold text-base leading-relaxed">
                  The result, year after year: children who express their thoughts clearly and speak to any audience, of any size, comfortably.
                </p>
              </div>
            </div>

            {/* Photo placeholder + existing image */}
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-3xl overflow-hidden shadow-md h-56 sm:h-64">
                <img src={IMG_YOGA_1} alt="Yoga — placeholder for school photo" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="rounded-3xl overflow-hidden shadow-md h-56 sm:h-64">
                <img src={IMG_ARTS_1} alt="Arts — placeholder for school photo" className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── THE WIDER WORLD ── */}
      <section className="py-20 bg-white" data-testid="wider-world-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">Beyond School Walls</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1E3A8A]" style={{ fontFamily: 'Nunito, sans-serif' }}>
              The wider world
            </h2>
          </div>
          <div className="space-y-4">
            {widerWorld.map((item, i) => (
              <div key={i} data-testid={`wider-world-${i}`} className="flex items-start gap-5 bg-[#F4F7FB] rounded-2xl px-7 py-6">
                <div className="w-10 h-10 rounded-2xl bg-[#DBEAFE] flex items-center justify-center flex-shrink-0 mt-0.5">
                  {item.icon}
                </div>
                <div>
                  <p className="font-bold text-[#0F172A] text-sm mb-1">{item.title}</p>
                  <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CELEBRATIONS ── */}
      <section className="py-20 bg-[#F4F7FB]" data-testid="celebrations-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            {/* Images */}
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-3xl overflow-hidden shadow-md h-52 sm:h-60">
                <img src={IMG_KARATE_1} alt="Placeholder for school celebration photo" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="rounded-3xl overflow-hidden shadow-md h-52 sm:h-60">
                <img src={IMG_ARTS_2} alt="Placeholder for school celebration photo" className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">All Year Round</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1E3A8A] mb-6 leading-tight" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Celebrations of every colour
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                We celebrate the festivals of all religions — and more importantly, we tell their stories. Children grow up knowing <em>why</em> each celebration exists, and learn early that every tradition carries beauty.
              </p>
              <div className="bg-white rounded-2xl px-6 py-5 border-l-4 border-[#1E3A8A] shadow-sm">
                <p className="text-[#1E3A8A] font-bold text-sm leading-relaxed">
                  The environment through the year is exactly what our founder built it to be: happy minds, happy energy, and a genuinely positive aura.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── ANNUAL DAY ── */}
      <section className="py-20 bg-white" data-testid="annual-day-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">The Highlight of the Year</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1E3A8A] mb-6 leading-tight" style={{ fontFamily: 'Nunito, sans-serif' }}>
                The Annual Day<br className="hidden sm:block" /> the children run
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Our Annual Day has one remarkable rule: <strong className="text-[#1E3A8A]">the children manage it — entirely.</strong>
              </p>
              <p className="text-slate-600 leading-relaxed mb-6">
                Planning, coordination, performance, responsibility. It is freedom and trust made visible on a stage, and there is no better single evening to understand what this school builds in a child.
              </p>
            </div>
            {/* Photo strip of existing images */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-3xl overflow-hidden shadow-md h-44">
                <img src={IMG_KARATE_2} alt="Placeholder for Annual Day photo" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="rounded-3xl overflow-hidden shadow-md h-44">
                <img src={IMG_YOGA_2} alt="Placeholder for Annual Day photo" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="col-span-2 rounded-3xl overflow-hidden shadow-md h-44">
                <img src={IMG_ARTS_1} alt="Placeholder for Annual Day photo" className="w-full h-full object-cover object-center" loading="lazy" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── COME WATCH ── */}
      <section className="py-20 bg-[#1E3A8A]" data-testid="activities-cta-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300 mb-4">Come and See</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-5" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Come watch a week in action
          </h2>
          <p className="text-blue-200 text-base mb-10 max-w-xl mx-auto">
            The timetable can't capture the joy. A visit can.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/admissions#enquiry-form"
              data-testid="activities-tour-btn"
              className="bg-white text-[#1E3A8A] rounded-full px-8 py-4 font-black text-sm hover:bg-blue-50 transition-all duration-200 hover:-translate-y-0.5 shadow-md inline-flex items-center justify-center gap-2"
            >
              Book a Tour <ArrowRight size={16} />
            </Link>
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="activities-whatsapp-btn"
              className="bg-[#25D366] text-white rounded-full px-8 py-4 font-black text-sm hover:bg-[#128C7E] transition-all duration-200 hover:-translate-y-0.5 inline-flex items-center justify-center gap-2"
            >
              <MessageCircle size={16} />
              <span className="flex flex-col items-start leading-tight">
                <span>WhatsApp</span>
                <span className="font-normal opacity-90" style={{ fontSize: '0.7rem' }}>+91 92468 87272</span>
              </span>
            </a>
            <a
              href="tel:9246887272"
              data-testid="activities-call-btn"
              className="border-2 border-white/40 text-white rounded-full px-8 py-4 font-black text-sm hover:border-white hover:bg-white/10 transition-all duration-200 hover:-translate-y-0.5 inline-flex items-center justify-center gap-2"
            >
              <Phone size={16} />
              <span className="flex flex-col items-start leading-tight">
                <span>Call</span>
                <span className="font-normal opacity-90" style={{ fontSize: '0.7rem' }}>+91 92468 87272</span>
              </span>
            </a>
          </div>
        </div>
      </section>

    </div>
  );
}
